<template lang="pug">
v-chart.insights-bar-chart(
  :option="chartOption"
  :style="{ height: `${chartHeight}px` }"
  autoresize
  role="img"
  :aria-label="ariaLabel"
)
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import VChart from 'vue-echarts';
import {
  CHART_COLOR, CHART_GRID_COLOR, CHART_TEXT_COLOR, type ValueFormatter,
} from './echarts';

export interface BarItem {
  label: string;
  value: number;
  // Extra tooltip line, e.g. "12 sales".
  detail?: string;
}

const ROW_HEIGHT = 32;

export default defineComponent({
  name: 'InsightsBarChart',
  components: { VChart },
  props: {
    items: { type: Array as PropType<BarItem[]>, default: () => [] },
    formatValue: {
      type: Function as PropType<ValueFormatter>,
      default: (value: number) => String(value),
    },
  },
  setup(props) {
    // Horizontal bars, ranked top to bottom (ECharts draws category axes bottom-up).
    const rows = computed(() => [...props.items].reverse());

    const chartHeight = computed(() => Math.max(rows.value.length, 1) * ROW_HEIGHT + 16);

    const chartOption = computed(() => ({
      animation: false,
      grid: {
        left: 8, right: 64, top: 8, bottom: 8, containLabel: true,
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: { dataIndex: number }) => {
          const item = rows.value[params.dataIndex];
          if (!item) return '';
          const detail = item.detail ? `<br/>${item.detail}` : '';
          return `${item.label}<br/><b>${props.formatValue(item.value)}</b>${detail}`;
        },
      },
      xAxis: {
        type: 'value',
        show: false,
        splitLine: { lineStyle: { color: CHART_GRID_COLOR } },
      },
      yAxis: {
        type: 'category',
        data: rows.value.map((item) => item.label),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: CHART_TEXT_COLOR,
          width: 160,
          overflow: 'truncate',
        },
      },
      series: [{
        type: 'bar',
        data: rows.value.map((item) => item.value),
        barMaxWidth: 16,
        itemStyle: { color: CHART_COLOR, borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          color: '#374151',
          formatter: (params: { value: number }) => props.formatValue(params.value),
        },
      }],
    }));

    const ariaLabel = computed(() => props.items
      .map((item) => `${item.label}: ${props.formatValue(item.value)}`)
      .join(', '));

    return { chartOption, chartHeight, ariaLabel };
  },
});
</script>

<style lang="scss" scoped>
.insights-bar-chart {
  width: 100%;
}
</style>
